/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import { useAuthtoken } from 'stores/useAuthStore';

import type {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid';

import { Base } from 'containers/Base';
import { useViewDoc } from 'containers/Docs/hooks/useDocWebDialog';
import type { DocumentStub } from 'containers/Docs/docs.types';
import type { Configuration } from 'containers/Schema/schema.types';
import type { TSchema } from 'types/items';
import type { WorkingState } from 'containers/Data/data.types';

import {
  useBaseViewData,
  type BaseViewDataOptions,
  type BaseViewParams,
} from './useBaseViewData';
import { useSchemaQuery } from './useSchemaQuery';

type Props = {
  configuration: Configuration;
  gridHeightCss?: string;
  customAdd?: BaseViewDataOptions['customAdd'];
  customChange?: BaseViewDataOptions['customChange'];
  customDelete?: BaseViewDataOptions['customDelete'];
  customList?: BaseViewDataOptions['customList'];
  onMutationSuccess?: BaseViewDataOptions['onMutationSuccess'];
  customSchema?: TSchema<any>;
};

export default function BaseView({
  configuration,
  gridHeightCss,
  customAdd,
  customChange,
  customDelete,
  customList,
  customSchema,
  onMutationSuccess,
}: Props) {
  const viewDoc = useViewDoc();
  const authtoken = useAuthtoken();
  const { schemaQuery, refetchSchema } = useSchemaQuery(
    configuration.itemType,
    authtoken,
    { enabled: !customSchema }
  );
  const schema = customSchema ?? schemaQuery.data;

  const [params, setParams] = useState<BaseViewParams | null>(null);

  const { listQuery, add, change, remove, exportRows } = useBaseViewData(
    configuration,
    params,
    authtoken,
    { customAdd, customChange, customDelete, customList, onMutationSuccess }
  );

  const lastUpdated = useMemo(
    () => (listQuery.dataUpdatedAt ? new Date(listQuery.dataUpdatedAt) : null),
    [listQuery.dataUpdatedAt]
  );

  const data = useMemo(
    () => ({
      values: listQuery.data?.values ?? [],
      rowCount: listQuery.data?.rowCount ?? -1,
      lastUpdated,
    }),
    [listQuery.data, lastUpdated]
  );

  const latestMutation = [add, change, remove].reduce((latest, m) =>
    (m.submittedAt ?? 0) >= (latest.submittedAt ?? 0) ? m : latest
  );

  const workingState = useMemo<WorkingState>(
    () => ({
      itemType: configuration.itemType,
      adding: add.isPending,
      updating: change.isPending,
      deleting: remove.isPending,
      loading: listQuery.isFetching,
      error: latestMutation.isError,
    }),
    [
      configuration.itemType,
      add.isPending,
      change.isPending,
      remove.isPending,
      listQuery.isFetching,
      latestMutation.isError,
    ]
  );

  const onDataRefresh = (
    _configuration: Configuration,
    disablePagination: boolean,
    forceRefresh: boolean,
    filterModel: GridFilterModel | undefined,
    globalSearch: string,
    paginationModel: GridPaginationModel | undefined,
    sortModel: GridSortModel | undefined
  ) => {
    setParams({
      disablePagination,
      forceRefresh,
      filterModel,
      globalSearch,
      paginationModel,
      sortModel,
    });
  };

  const onDataAdd = (
    _c: Configuration,
    _dp: boolean,
    _fr: boolean,
    _fm: GridFilterModel | undefined,
    _gs: string,
    _pm: GridPaginationModel | undefined,
    rows: any[],
    rowSchema: TSchema<any>
  ) => add.mutate({ rows, schema: rowSchema });

  const onDataChange = (
    _c: Configuration,
    _dp: boolean,
    _fr: boolean,
    _fm: GridFilterModel | undefined,
    _gs: string,
    _pm: GridPaginationModel | undefined,
    rows: any[],
    rowSchema: TSchema<any>
  ) => change.mutate({ rows, schema: rowSchema });

  const onDataDelete = (
    _c: Configuration,
    _dp: boolean,
    _fr: boolean,
    _fm: GridFilterModel | undefined,
    _gs: string,
    _pm: GridPaginationModel | undefined,
    rows: any[],
    rowSchema: TSchema<any>
  ) => remove.mutate({ rows, schema: rowSchema });

  const onDataExport = (
    _c: Configuration,
    _dp: boolean,
    _fr: boolean,
    _fm: GridFilterModel | undefined,
    _gs: string,
    _pm: GridPaginationModel | undefined,
    rows: any[]
  ) => exportRows(rows);

  const onDataClear = () => undefined;

  const onSchemaRefresh = () => {
    if (customSchema) return;
    refetchSchema();
  };
  const onDocView = (doc: DocumentStub) => viewDoc.mutate(doc);

  if (!schema) return null;

  return (
    <Base
      authtoken={authtoken}
      configuration={configuration}
      data={data}
      gridHeightCss={gridHeightCss}
      schema={schema}
      workingState={workingState}
      onDataAdd={onDataAdd}
      onDataChange={onDataChange}
      onDataClear={onDataClear}
      onDataDelete={onDataDelete}
      onDataExport={onDataExport}
      onDataRefresh={onDataRefresh}
      onDocView={onDocView}
      onSchemaRefresh={onSchemaRefresh}
    />
  );
}
